import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { fetchNFCHomepage, fetchUserPicture } from '../../api/mobileapi';
import { HomeData } from '../../api/types';
import { AdamsError } from '../../utils/errors';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { HideInProduction } from '../HideInProduction/HideInProduction';
import { useHomeData } from '../LoadHomeData/HomeDataProvider';
import { NFCMockForm } from '../NFCMockForm/NFCMockForm';
import { NFCScan } from '../NFCScan/NFCScan';
import { NFCScanIndicator } from '../NFCScanIndicator/NFCScanIndicator';
import { NFCScanResult } from '../NFCScanResult/NFCScanResult';

interface NFCDetectProps {
    translations: Record<string, string>;
}

export const NFCDetect: React.FC<NFCDetectProps> = ({ translations }) => {
    const { currentLanguage } = useHomeData();
    const [rfid, setRfid] = useState<string | null>(null);
    const [scanError, setScanError] = useState<string | null>(null);
    const [requestError, setRequestError] = useState<AdamsError | null>(null);
    const [nfcData, setNfcData] = useState<HomeData | null>(null);
    const location = useLocation();

    useEffect(() => {
        setNfcData(null);
        setScanError(null);
        setRequestError(null);
        setRfid(null);
    }, [location]);

    const {
        data,
        error: nfcError,
        isLoading: nfcLoading,
        fetchStatus,
    } = useQuery<HomeData, AdamsError>(
        ['nfcDetect', rfid?.toString()],
        () => fetchNFCHomepage({ rfid: rfid!, language: currentLanguage }),
        {
            enabled: !!rfid,
        }
    );

    const {
        data: scannedUserPicture,
        error: pictureError,
        isLoading: pictureLoading,
    } = useQuery<string, AdamsError>(['userPicture', nfcData], () => fetchUserPicture(nfcData!), {
        enabled: !!nfcData,
    });

    useEffect(() => {
        if (data) {
            setNfcData(data);
        } else if (nfcError || pictureError) {
            setRequestError(nfcError || pictureError);
        }
    }, [data, nfcError, pictureError]); //so ugly, TODO

    const loading = (nfcLoading || pictureLoading) && fetchStatus !== 'idle';
    const errorMessage = nfcError?.message || pictureError?.message || '';

    const reset = useCallback(() => {
        setRfid(null);
        setScanError(null);
        setNfcData(null);
    }, [setNfcData, setRfid, setScanError]);

    const handleError = useCallback((err: string) => {
        setScanError(err);
    }, []);

    const handleDetect = useCallback(
        (result: React.SetStateAction<string | null>) => {
            if (result) {
                setScanError(null);
                setRfid(result);
            } else {
                handleError('invalid scan');
            }
        },
        [handleError]
    );

    if (nfcData) {
        return (
            <>
                <NFCScanResult
                    data={nfcData}
                    translations={translations}
                    scannedUserPicture={scannedUserPicture}
                    setRfid={setRfid}
                    onReset={reset}
                />
                <NFCScan onScan={handleDetect} onError={handleError} />
            </>
        );
    }

    return (
        <>
            <HideInProduction>
                <NFCMockForm onSubmit={handleDetect} />
            </HideInProduction>

            <NFCScan onScan={handleDetect} onError={handleError} />

            {scanError && !loading && (
                <ErrorMessage
                    errorMessage={JSON.stringify(scanError)}
                    showReturnButton={false}
                    title={translations.GLOBAL_ERROR_TITLE}
                    translations={translations}
                />
            )}
            {requestError && !loading && (
                <ErrorMessage
                    errorMessage={errorMessage}
                    showReturnButton={false}
                    title={translations.GLOBAL_ERROR_TITLE}
                    translations={translations}
                />
            )}

            {loading && <LinearProgress />}

            <Box my={4}>
                <Card>
                    <CardContent>
                        <NFCScanIndicator translations={translations} />
                    </CardContent>
                </Card>
            </Box>
        </>
    );
};
